import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import { capitalize } from "lodash";
import moment from "moment";
import { FC, useCallback, useState } from "react";
import { HiOutlineEye } from "react-icons/hi";
import { HiOutlinePencil } from "react-icons/hi";
import { TbToggleLeft, TbToggleRight } from "react-icons/tb";
import { ActionIcon, Badge, Tooltip } from "rizzui";
import { toast } from "sonner";
import { CustomBadge } from "../../../components/forms/CustomBadge";
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import CustomTable from "../../../components/table/CustomTable";
import useFetch from "../../../hooks/useFetch";
import Page from "../../../layout/Page/Page";
import { PickupGlobalMessageService } from "../../../services/pickup/pickupGlobalMessageService";
import { PickupGlobalMessage, PickupGlobalMessagesApiResponse } from "../../../type/entities/pickup-global-message-type";
import cn from "../../../utils/classNames";
import { pickupMessagesTypes } from "../../../utils/SelectorMapping";
import PickupGlobalMessageForm from "../components/PickupGlobalMessageForm";
import PickupGlobalMessageFilters from "./PickupGlobalMessageFilters";

interface PickupGlobalMessagesListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    messageSelected: PickupGlobalMessage | null;
    setMessageSelected: React.Dispatch<React.SetStateAction<PickupGlobalMessage | null>>;
}

const columnOrder = [
    'id',
    'concept',
    'message',
    'type',
    'color',
    'startDate',
    'endDate',
    'isActive',
    'company',
    'creator',
    'createdAt',
    'updatedAt'
];

const PickupGlobalMessagesList: FC<PickupGlobalMessagesListProps> = ({
    openFormDrawer,
    setOpenFormDrawer,
    messageSelected,
    setMessageSelected
}) => {

    // STATES
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const service = new PickupGlobalMessageService();

    // HOOKS
    const { userCan } = usePrivilege();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    // FETCH DATA
    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE MENSAJES GLOBALES
     * @EN GETS THE LIST OF GLOBAL MESSAGES
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listPickupGlobalMessages(filters);
        return response.getResponseData() as PickupGlobalMessagesApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA UN MENSAJE GLOBAL
     * @EN DELETES A GLOBAL MESSAGE
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await service.deletePickupGlobalMessage(id);
            const responseData = response.getResponseData();
            if (responseData.success) {
                refetch();
                toast.success(responseData.message || 'Mensaje global eliminado con éxito');
            } else {
                toast.error(responseData.message || 'Error al eliminar el mensaje global');
            }
        } catch (error: any) {
            toast.error(error.message || 'Error al eliminar el mensaje global');
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA MÚLTIPLES MENSAJES GLOBALES
     * @EN DELETES MULTIPLE GLOBAL MESSAGES
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultipleDelete = async (ids: string[]) => {
        try {
            if (ids.length === 0) {
                toast.error('No se ha seleccionado ningún mensaje global válido para eliminar');
                return;
            }
            const response = await service.deleteMultiPickupGlobalMessages(ids);
            const responseData = response.getResponseData();
            if (responseData.success) {
                refetch();
                toast.success(responseData.message || 'Mensajes globales eliminados con éxito');
            } else {
                toast.error(responseData.message || 'Error al eliminar los mensajes globales');
            }
        } catch (error: any) {
            toast.error(error.message || 'Error al eliminar los mensajes globales');
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ACTIVA/DESACTIVA UN MENSAJE GLOBAL
     * @EN TOGGLES A GLOBAL MESSAGE ACTIVE STATUS
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleToggleActive = async (id: string) => {
        try {
            const response = await service.togglePickupGlobalMessage(id);
            const responseData = response.getResponseData();
            if (responseData.success) {
                refetch();
                toast.success(responseData.message || 'Estado del mensaje actualizado con éxito');
            } else {
                toast.error(responseData.message || 'Error al actualizar el estado del mensaje');
            }
        } catch (error: any) {
            toast.error(error.message || 'Error al actualizar el estado del mensaje');
        }
    };
    //-------------------------------------------------------------------------------------------------------------



    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    return (
        <Page container='fluid'>

            {data !== undefined && (
                <CustomTable
                    id="pickup-global-messages-table"
                    data={orderedData()}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    floatingHorizontalScrollbar
                    columnOrder={columnOrder}
                    isLoading={loading}
                    isSortable={true}
                    pagination={true}
                    search={true}
                    filter={true}
                    overrideColumns={[
                        {
                            key: 'id',
                            render: (row: any) => (
                                <span className="font-mono text-xs">{row.row.original.id.slice(0, 8)}...</span>
                            )
                        },
                        {
                            key: 'concept',
                            columnNameKey: 'Concepto',
                            render: (row: any) => (
                                <span className="text-sm font-medium capitalize">{row.row.original.concept}</span>
                            )
                        },
                        {
                            key: 'type',
                            columnNameKey: 'Tipo',
                            render: (row: any) => {
                                const color = row.row.original.color || 'bg-gray-100 text-gray-800';
                                const type = pickupMessagesTypes.find((item) => item.value === row.row.original.type);

                                return (
                                    <>
                                        {type ? (
                                            <CustomBadge
                                                className={`px-4 text-xs`}
                                                variant="solid"
                                                color={type.color as "info" | "primary" | "warning" | "danger" | "success" | "secondary" | undefined}
                                            >
                                                {type?.label || row.row.original.type}
                                            </CustomBadge>
                                        ) :
                                        (
                                            <span className="px-4 text-xs bg-red-100 text-red-800">No definido</span>
                                        )}
                                    </>
                                );
                            }
                        },
                        {
                            key: 'message',
                            columnNameKey: 'Mensaje',
                            render: (row: any) => (
                                <div className="max-w-xs truncate" title={row.row.original.message}>
                                    {row.row.original.message}
                                </div>
                            )
                        },
                        {
                            key: 'color',
                            render: (row: any) => (
                                <div className="flex items-center gap-2">
                                    <div
                                        className="w-4 h-4 rounded border"
                                        style={{ backgroundColor: row.row.original.color }}
                                    />
                                    <span className="text-xs font-mono">{row.row.original.color}</span>
                                </div>
                            )
                        },
                        {
                            key: 'startDate',
                            render: (row: any) => (
                                <span className="text-sm">
                                    {moment(row.row.original.startDate.date).format('DD/MM/YYYY HH:mm')}
                                </span>
                            )
                        },
                        {
                            key: 'endDate',
                            render: (row: any) => (
                                <span className="text-sm">
                                    {row.row.original.endDate ? moment(row.row.original.endDate.date).format('DD/MM/YYYY HH:mm') : 'Sin fecha fin'}
                                </span>
                            )
                        },
                        {
                            key: 'isActive',
                            columnNameKey: 'Estado',
                            render: (row: any) => (
                                <Badge
                                    variant="flat"
                                    color={row.row.original.isActive ? 'success' : 'danger'}
                                >
                                    {row.row.original.isActive ? 'Activo' : 'Inactivo'}
                                </Badge>
                            )
                        },
                        {
                            key: 'company',
                            render: (row: any) => (
                                <div>
                                    <div className="font-medium text-sm">{row.row.original.company?.name}</div>
                                    <div className="text-xs text-gray-500">{row.row.original.company?.cif}</div>
                                </div>
                            )
                        },
                        {
                            key: 'creator',
                            columnNameKey: 'creator',
                            render: (row: any) => (
                                <span className="text-sm">
                                    {row.row.original.creator?.name} {row.row.original.creator?.lastName}
                                </span>
                            )
                        },
                        {
                            key: 'createdAt',
                            render: (row: any) => (
                                <span className="text-sm">
                                    {moment(row.row.original.createdAt).format('DD/MM/YYYY HH:mm')}
                                </span>
                            )
                        },
                        {
                            key: 'updatedAt',
                            render: (row: any) => {
                                const date = row.row.original?.updatedAt?.date;
                                return date ? (
                                    <div className="text-sm text-gray-500">
                                        <Tooltip content={moment(date).format('DD/MM/YYYY HH:mm:ss')}>
                                            <span>{capitalize(formatDistance(new Date(date), new Date(), { addSuffix: true, locale: es }))}</span>
                                        </Tooltip>
                                    </div>
                                ) : 'Nunca';
                            },
                        },
                    ]}
                    actions={[
                        // {
                        //     label: 'Ver',
                        //     permissions: { group: 'pickup', permission: 'get' },
                        //     render: (row: any) => (
                        //         <Tooltip
                        //             size="sm"
                        //             content={'Ver Mensaje'}
                        //             placement="top"
                        //             color="invert"
                        //         >
                        //             <ActionIcon
                        //                 onClick={() => {
                        //                     setMessageSelected(row);
                        //                     setOpenFormDrawer(true);
                        //                 }}
                        //                 as="span"
                        //                 size="sm"
                        //                 variant="solid"
                        //                 className="hover:!border-gray-900 text-gray-500 bg-white"
                        //             >
                        //                 <HiOutlineEye className="h-4 w-4" />
                        //             </ActionIcon>
                        //         </Tooltip>
                        //     ),
                        // },
                        {
                            label: 'Editar',
                            permissions: { group: 'pickup', permission: 'edit' },
                            render: (row: any) => (
                                <Tooltip
                                    size="sm"
                                    content={'Editar Mensaje'}
                                    placement="top"
                                    color="invert"
                                >
                                    <ActionIcon
                                        onClick={() => {
                                            setMessageSelected(row);
                                            setOpenFormDrawer(true);
                                        }}
                                        as="span"
                                        size="sm"
                                        variant="solid"
                                        className="hover:!border-gray-900 text-gray-500 bg-white"
                                    >
                                        <HiOutlinePencil className="h-4 w-4" />
                                    </ActionIcon>
                                </Tooltip>
                            ),
                        },
                        {
                            label: 'Toggle Active',
                            permissions: { group: 'pickup', permission: 'edit' },
                            render: (row: any) => (
                                <Tooltip
                                    size="sm"
                                    content={row.isActive ? 'Desactivar' : 'Activar'}
                                    placement="top"
                                    color="invert"
                                >
                                    <ActionIcon
                                        onClick={() => handleToggleActive(row.id)}
                                        as="span"
                                        size="sm"
                                        variant="solid"
                                        className={cn(
                                            "hover:!border-gray-900 hover:text-white",
                                            row.isActive ? "bg-green-500" : "bg-gray-500"
                                        )}
                                    >
                                        {row.isActive ? (
                                            <TbToggleRight className="h-4 w-4" />
                                        ) : (
                                            <TbToggleLeft className="h-4 w-4" />
                                        )}
                                    </ActionIcon>
                                </Tooltip>
                            ),
                        }
                    ]}
                    multiActions={[
                        {
                            label: 'Eliminar seleccionados',
                            permissions: { group: 'pickup', permission: 'delete' },
                            render: handleMultipleDelete
                        }
                    ]}
                    handleMultipleDelete={handleMultipleDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as PickupGlobalMessagesApiResponse)?.lastPage || 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                >
                </CustomTable>
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Mensajes Globales'} onClear={() => resetFilters(50)}>
                <PickupGlobalMessageFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    messageSelected ?
                        "Completa el formulario para editar los datos del mensaje global" :
                        "Completa el formulario para crear un nuevo mensaje global"
                }
                drawerTitle={messageSelected ? 'Editar Mensaje Global' : 'Crear Mensaje Global'}
                submitText={messageSelected ? 'Guardar Mensaje' : 'Crear Mensaje'}
            >
                <PickupGlobalMessageForm
                    open={openFormDrawer}
                    onClose={() => {
                        setOpenFormDrawer(false);
                        setMessageSelected(null);
                        refetch();
                    }}
                    pickupGlobalMessageSelected={messageSelected}
                />
            </FormDrawerView>

        </Page>
    );
};

export default PickupGlobalMessagesList;